import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { BALANCE_WS_URL } from "../config/index";
import { getStoredAuthToken } from "../utils/authToken";
import {
  applyBalanceUpdate,
  getBalanceCard,
  refreshJwtToken,
} from "../features/slices/UserSlice";

const PING_INTERVAL_MS = 25000;
const RECONNECT_DELAYS_MS = [1000, 2000, 5000, 10000, 30000];
const POLICY_VIOLATION = 1008;

/**
 * Keeps the header balance live while the player is logged in.
 * wss://.../ws/balance?token=<player access token> pushes `balance:updated`
 * on every wallet change; after each (re)connect we also GET /player/balance
 * once so nothing sent while disconnected is missed.
 */
export default function useBalanceSocket() {
  const dispatch = useDispatch();
  const loggedIn = useSelector((state) => state.user.userLoggedIn);
  const depositSubmitted = useSelector(
    (state) => state.payment.depositRequestedSuccessfully
  );

  useEffect(() => {
    if (!loggedIn) return undefined;

    let ws = null;
    let pingTimer = null;
    let reconnectTimer = null;
    let attempt = 0;
    let unauthorized = false;
    let opened = false;
    let refreshTried = false;
    let stopped = false;

    const scheduleReconnect = () => {
      if (stopped) return;
      const delay =
        RECONNECT_DELAYS_MS[Math.min(attempt, RECONNECT_DELAYS_MS.length - 1)];
      attempt += 1;
      reconnectTimer = setTimeout(connect, delay);
    };

    // bad/expired token: refresh it once per failure streak, then keep retrying
    // with backoff (a failed refresh may just be the network being down)
    const refreshAndReconnect = async () => {
      refreshTried = true;
      await dispatch(refreshJwtToken());
      scheduleReconnect();
    };

    function connect() {
      const token = getStoredAuthToken();
      if (stopped || !token) return;
      unauthorized = false;
      opened = false;

      ws = new WebSocket(
        `${BALANCE_WS_URL}?token=${encodeURIComponent(token)}`
      );

      ws.onopen = () => {
        opened = true;
        refreshTried = false;
        attempt = 0;
        pingTimer = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: "ping" }));
          }
        }, PING_INTERVAL_MS);
        dispatch(getBalanceCard());
      };

      ws.onmessage = (event) => {
        let message;
        try {
          message = JSON.parse(event.data);
        } catch {
          return;
        }
        if (message?.event === "balance:updated") {
          dispatch(applyBalanceUpdate(message.data));
        } else if (message?.event === "error") {
          unauthorized = true;
        }
      };

      ws.onclose = (event) => {
        clearInterval(pingTimer);
        ws = null;
        if (stopped) return;
        // the server rejects a bad token with HTTP 403 before the upgrade,
        // which the browser reports as a close without ever opening
        const rejected =
          unauthorized || event.code === POLICY_VIOLATION || !opened;
        if (rejected && !refreshTried) {
          refreshAndReconnect();
        } else {
          scheduleReconnect();
        }
      };
    }

    connect();

    return () => {
      stopped = true;
      clearInterval(pingTimer);
      clearTimeout(reconnectTimer);
      if (ws) ws.close();
    };
  }, [loggedIn, dispatch]);

  // safety net after our own deposit request
  useEffect(() => {
    if (loggedIn && depositSubmitted) dispatch(getBalanceCard());
  }, [loggedIn, depositSubmitted, dispatch]);
}
